import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { App, Button, Card, Modal, Popconfirm, Select, Space, Table, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { NameCell, TableSearch } from '../../components/NameCell';
import type { ContactListItem } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { useListParams } from '../../components/list-params';
import { downloadCsv } from '../../components/export-csv';
import { ExportButton } from '../../components/ExportButton';
import { useFields } from '../../features/fields/api';
import { contactName } from '../../features/contacts/api';
import { useDeleteList, useList, useListContacts, useListMembers } from '../../features/audience/api';
import { api, errorMessage } from '../../api/client';
import { renderCell } from '../contacts/ContactsPage';
import { EmptyState, PageSkeleton, RecordError, tableLoading, tableLocale } from '../../components/states';
import { plural } from '../../utils/format';
import { QuickCreateModal } from '../../components/QuickCreateModal';

export function ListDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const list = useList(id);
  const { params, setParams } = useListParams({ sortBy: 'last_name', sortDir: 'asc' });
  const contacts = useListContacts(id, params);
  const defs = useFields('CONTACT');
  const members = useListMembers();
  const remove = useDeleteList();
  const [selected, setSelected] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);

  if (list.isError) return <RecordError error={list.error} what="List" backTo="/lists" backLabel="Back to lists" onRetry={list.refetch} />;
  if (!list.data) return <PageSkeleton />;
  const l = list.data;
  const cols = (defs.data ?? []).filter((d) => ['job_title', 'organization_id', 'lifecycle_stage', 'email_status'].includes(d.key));
  const listFilter = { combinator: 'and' as const, conditions: [{ kind: 'list' as const, op: 'in_any' as const, listIds: [l.id] }] };

  const removeSelected = async () => {
    try {
      const r = await members.mutateAsync({ listId: l.id, action: 'remove', selection: { ids: selected } });
      message.success(`Removed ${plural(r.removed ?? 0, 'contact')} from the list`);
      setSelected([]);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  return (
    <div className="page">
      <PageHeader
        breadcrumb={[{ title: 'Lists', to: '/lists' }, { title: l.name }]}
        title={l.name}
        subtitle={<span>{plural(l.memberCount, 'member')}{l.description ? ` · ${l.description}` : ''}</span>}
        extra={
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>Add contacts</Button>
            <Popconfirm title="Delete this list? Contacts are kept." okText="Delete" okButtonProps={{ danger: true, loading: remove.isPending }} onConfirm={() => remove.mutateAsync(l.id).then(() => { message.success('List deleted'); navigate('/lists'); }).catch((e) => message.error(errorMessage(e)))}>
              <Button danger icon={<DeleteOutlined />} aria-label="Delete list" title="Delete list" />
            </Popconfirm>
          </Space>
        }
      />
      <Card>
      <div className="table-toolbar">
        <div className="toolbar-group">
          <TableSearch value={params.q} onSearch={(q) => setParams({ q: q || undefined })} placeholder="Search members" width={300} />
        </div>
        <div className="toolbar-group">
          {selected.length > 0 && (
            <Popconfirm title={`Remove ${plural(selected.length, 'contact')} from this list?`} okText="Remove" okButtonProps={{ loading: members.isPending }} onConfirm={() => removeSelected()}>
              <Button>Remove {selected.length} from list</Button>
            </Popconfirm>
          )}
          <ExportButton onExport={() => downloadCsv('/contacts/export.csv', params, { filter: JSON.stringify(listFilter) })} count={contacts.data?.total} noun="contact" />
        </div>
      </div>
      <Table<ContactListItem>
        rowKey="id"
        size="middle"
        loading={tableLoading(contacts)}
        scroll={{ x: 'max-content' }}
        locale={tableLocale(
          contacts,
          params.q ? 'No members match this search.' : <EmptyState description="This list is empty. Add contacts here, or use bulk actions on the Contacts page." action={<Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>Add contacts</Button>} />,
        )}
        dataSource={contacts.data?.items ?? []}
        rowSelection={{ selectedRowKeys: selected, onChange: (k) => setSelected(k as string[]) }}
        pagination={{ current: params.page, pageSize: params.pageSize, total: contacts.data?.total ?? 0, onChange: (page, pageSize) => setParams({ page, pageSize }) }}
        columns={[
          { title: 'Name', key: 'name', render: (_, c) => <NameCell name={contactName(c)} sub={c.email} to={`/contacts/${c.id}`} /> },
          ...cols.map((f) => ({ title: f.label, key: f.key, ellipsis: true, render: (_: unknown, c: ContactListItem) => renderCell(f, c) })),
        ]}
      />
      </Card>
      <AddContactsModal open={adding} onClose={() => setAdding(false)} onAdd={async (ids) => {
        const r = await members.mutateAsync({ listId: l.id, action: 'add', selection: { ids } });
        message.success(`Added ${plural(r.added ?? 0, 'contact')}`);
      }} />
    </div>
  );
}

/** Pick contacts by search and add them to the list. Bulk-adding by filter is done from the Contacts page. */
function AddContactsModal({ open, onClose, onAdd }: { open: boolean; onClose: () => void; onAdd: (ids: string[]) => Promise<void> }) {
  const { message } = App.useApp();
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [picked, setPicked] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const timer = useRef(0);
  const seq = useRef(0);
  const search = (q: string) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      const mine = ++seq.current;
      setLoading(true);
      try {
        const r = await api.get<{ items: ContactListItem[] }>('/contacts', { params: { q, pageSize: 20 } });
        if (mine === seq.current) setOptions(r.data.items.map((c) => ({ value: c.id, label: `${contactName(c)}${c.email ? ` <${c.email}>` : ''}` })));
      } catch (e) {
        if (mine === seq.current) message.error(errorMessage(e));
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, 250);
  };
  const close = () => {
    setPicked([]);
    onClose();
  };
  return (
    <Modal
      title="Add contacts to list"
      open={open}
      onCancel={close}
      okText={picked.length ? `Add ${plural(picked.length, 'contact')}` : 'Add'}
      okButtonProps={{ disabled: !picked.length }}
      confirmLoading={busy}
      onOk={async () => {
        setBusy(true);
        try {
          await onAdd(picked.map((p) => p.value));
          close();
        } catch (e) {
          message.error(errorMessage(e));
        } finally {
          setBusy(false);
        }
      }}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">Search by name, email or organization. To add everyone matching a filter, use the Contacts page bulk actions.</Typography.Paragraph>
      <Space.Compact style={{ width: '100%' }}>
        <Select
          mode="multiple"
          labelInValue
          showSearch
          filterOption={false}
          onSearch={search}
          onFocus={() => search('')}
          loading={loading}
          value={picked}
          onChange={(v) => setPicked(v as { value: string; label: string }[])}
          options={options}
          notFoundContent={loading ? 'Searching…' : 'No contacts found'}
          style={{ flex: 1, minWidth: 0 }}
          placeholder="Type to search contacts"
        />
        <Button icon={<PlusOutlined />} onClick={() => setCreating(true)}>
          New contact
        </Button>
      </Space.Compact>
      <QuickCreateModal
        objectType="CONTACT"
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(c) => setPicked((p) => [...p, { value: c.id, label: `${contactName(c)}${c.email ? ` <${c.email}>` : ''}` }])}
      />
    </Modal>
  );
}
